/**
 * OQ-136 · Khối "Sao lưu & khôi phục" trong Cài đặt hệ thống.
 *
 * Chủ dự án yêu cầu dựng cái này TRƯỚC khi thao tác phá huỷ trên dữ liệu thật.
 *
 * Hai điều màn hình phải nói thẳng, vì hiểu nhầm ở đây rất đắt:
 *
 *  1. Đây là sao lưu **CSDL DNSHub**, KHÔNG phải sao lưu **Cloudflare**. Bản ghi
 *     DNS thật nằm trên Cloudflare; khôi phục ở đây không đẩy gì ngược lên đó.
 *  2. Khôi phục **ghi đè toàn bộ** CSDL hiện tại. Nên nút ấy đòi gõ đúng tên bản
 *     sao lưu — cùng kiểu xác nhận như xoá tên miền, vì hậu quả tương đương.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_PREFIX, api } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { ApiErrorAlert } from '../../components/ApiErrorAlert';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';

interface BackupFile {
  name: string;
  sizeBytes: number;
  createdAt: string;
  /** OQ-303 — `null` cho tệp tạo trước khi lý do được ghi vào tên. */
  reason?: 'auto' | 'manual' | 'safety' | null;
}

/** `POST /settings/backups/:name/verify` — OQ-171. */
export interface BackupCheck {
  name: string;
  /** Mở được VÀ đọc được. **Không** bao hàm "có dữ liệu" — xem `warning`. */
  ok: boolean;
  /** Mở được nhưng nội dung đáng ngờ (vd. không có tên miền nào). */
  warning: string | null;
  error: string | null;
  tables: number;
  totalRows: number;
  counts: Record<string, number>;
  checkedAt: string;
}

/**
 * BA kết quả, không phải hai — OQ-147.
 *
 * `ok` một mình **không** đủ để tô xanh: một bản sao lưu 4,9 MB mở tốt, đọc
 * tốt, và không có một tên miền nào vẫn trả `ok: true`. Cột hai màu sẽ đặt nó
 * cạnh bản 77 tên miền với cùng một dấu tích xanh — *"4,5 MB của hư không cũng
 * là 4,5 MB"*.
 *
 * Nên phép phân loại phải đọc **cả** `ok` **và** `warning`.
 */
type KetQua = 'dat' | 'rong' | 'hong';

export function phanLoaiBanSaoLuu(check: BackupCheck): KetQua {
  if (!check.ok) return 'hong';
  if (check.warning) return 'rong';
  return 'dat';
}

/**
 * `docs/08 §8.7`: *"trạng thái không chỉ dựa vào màu (kèm chữ/icon)"* — nên mỗi
 * kết quả mang **ba** dấu hiệu độc lập: lớp màu, biểu tượng, và chữ.
 */
const DAU_HIEU: Record<KetQua, { chip: string; icon: string; nhan: string }> = {
  dat: { chip: 'ok', icon: 'check', nhan: 'settings.backup.check.pass' },
  rong: { chip: 'warn', icon: 'warn', nhan: 'settings.backup.check.empty' },
  hong: { chip: 'bad', icon: 'close', nhan: 'settings.backup.check.broken' },
};

/** Bảng đáng đếm riêng — trùng `BANG_QUAN_TRONG` của `backup.service.ts`. */
const BANG_HIEN = ['domains', 'users', 'cf_connections', 'dns_records'] as const;

interface RestoreResult {
  rowsRestored: number;
  tables: number;
  safetyBackup: string;
}

/**
 * Kích thước tệp cho người đọc.
 *
 * Xuất ra để `RemoteBackupPanel` dùng lại — OQ-273. Hai bảng cạnh nhau hiện
 * cùng một loại số thì phải hiện giống hệt nhau; viết bản thứ hai là chuốc lấy
 * chuyện "5.2 MB" ở bảng này và "5324 KB" ở bảng kia.
 */
export function coChu(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Ô trạng thái — bốn cách hiện, ba trong đó là kết quả kiểm thật.
 *
 * "Chưa kiểm" là trạng thái thứ tư và nó **phải** khác "đạt": mặc định xanh cho
 * tới khi chứng minh ngược lại chính là lỗi mà cột này sinh ra để chặn.
 *
 * Export để hộp thoại kho ngoài dùng lại (OQ-303). Một bản sao lưu hỏng phải
 * trông GIỐNG NHAU dù nó nằm trên máy chủ hay trên Dropbox — hai cách vẽ khác
 * nhau cho cùng một sự thật là cách dạy người đọc rằng hai chỗ ấy khác nhau.
 */
export function OTrangThai({
  check,
  dangKiem,
}: {
  check: BackupCheck | undefined;
  dangKiem: boolean;
}): JSX.Element {
  const { t } = useI18n();

  if (dangKiem) {
    return (
      <span className="chip" role="status">
        {t('settings.backup.check.running')}
      </span>
    );
  }
  if (!check) {
    return <span className="chip muted">{t('settings.backup.check.unknown')}</span>;
  }

  const loai = phanLoaiBanSaoLuu(check);
  const dau = DAU_HIEU[loai];

  return (
    <div className="stack" style={{ gap: 4 }}>
      <span className={`chip ${dau.chip}`}>
        <Icon name={dau.icon} size={13} />
        {t(dau.nhan)}
      </span>
      {loai === 'hong' ? (
        <span className="sub">{check.error ?? t('settings.backup.check.brokenUnknown')}</span>
      ) : (
        <span className="sub">
          {BANG_HIEN.filter((b) => check.counts[b] !== undefined)
            .map((b) => `${check.counts[b]} ${t(`settings.backup.count.${b}`)}`)
            .join(' · ')}
        </span>
      )}
    </div>
  );
}

/**
 * Hộp xác nhận khôi phục — dùng chung cho bản tại chỗ VÀ kho ngoài (OQ-304).
 *
 * **Chủ dự án**: *"khi khôi phục từ local thì có cảnh báo nhưng khôi phục từ
 * dropbox thì không"*.
 *
 * Đúng, và đó là bất đối xứng nguy hiểm nhất có thể có: **cùng một thao tác ghi
 * đè toàn bộ CSDL**, một đường bắt gõ lại tên tệp, một đường bấm phát chạy. Rào
 * chắn ở đường này dạy người dùng rằng khôi phục là việc phải cân nhắc — rồi
 * đường kia phản bội đúng bài học ấy.
 *
 * Tách thành component thay vì chép sang: hai bản chép sẽ lệch nhau theo thời
 * gian, và cách lệch đầu tiên chính là cái vừa xảy ra — một bên được thêm rào,
 * bên kia bị quên.
 */
export function XacNhanKhoiPhuc({
  ten,
  check,
  dangChay,
  onHuy,
  onXacNhan,
  odId,
}: {
  ten: string;
  check: BackupCheck | undefined;
  dangChay: boolean;
  onHuy: () => void;
  onXacNhan: () => void;
  odId?: string;
}): JSX.Element {
  const { t } = useI18n();
  const [goLai, setGoLai] = useState('');

  return (
    <Modal open title={t('settings.backup.confirm.title')} onClose={onHuy}>
      <div className="form-alert bad" role="alert">
        <Icon name="warn" size={16} />
        <span>{t('settings.backup.confirm.warn')}</span>
      </div>

      {/*
       * Nếu bản này ĐÃ kiểm và không đạt, nói ngay tại đây — OQ-147.
       *
       * Người dùng bấm Kiểm, thấy "hỏng", rồi vài giây sau bấm Khôi phục ở
       * cùng một hàng. Hộp thoại im lặng nghĩa là kết quả kiểm chỉ tồn tại ở
       * chỗ nó được hiện, không đi theo tới lúc nó có ích nhất.
       *
       * KHÔNG chặn nút: người vận hành có thể cố tình khôi phục một bản rỗng để
       * dọn sạch, và một bản "hỏng" vẫn có thể phục hồi được phần nào. Nói sự
       * thật rồi để họ quyết — chặn là thay họ quyết.
       */}
      {check && phanLoaiBanSaoLuu(check) !== 'dat' ? (
        <div className="form-alert warn" role="alert" data-od-id="backup-confirm-check">
          <Icon name="warn" size={16} />
          <span>
            {t(DAU_HIEU[phanLoaiBanSaoLuu(check)].nhan)}
            {' — '}
            {check.warning ?? check.error}
          </span>
        </div>
      ) : null}

      <p>{t('settings.backup.confirm.hint', { name: ten })}</p>
      <input
        type="text"
        value={goLai}
        onChange={(e) => setGoLai(e.target.value)}
        placeholder={ten}
        aria-label={t('settings.backup.confirm.title')}
        {...(odId ? { 'data-od-id': `${odId}-input` } : {})}
      />
      <div className="row" style={{ gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={onHuy}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-danger"
          disabled={goLai !== ten || dangChay}
          onClick={onXacNhan}
          {...(odId ? { 'data-od-id': odId } : {})}
        >
          {dangChay ? t('common.processing') : t('settings.backup.restore')}
        </button>
      </div>
    </Modal>
  );
}

export function BackupPanel(): JSX.Element {
  const { t } = useI18n();
  const { toast, toastError } = useToast();
  const [items, setItems] = useState<BackupFile[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [xacNhan, setXacNhan] = useState<BackupFile | null>(null);
  const [ketQua, setKetQua] = useState<RestoreResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  /* Câu ngắn cho toast — `ApiErrorAlert` ở trên vẫn giữ nguyên chi tiết đầy đủ. */
  const loiGon = (err: unknown): string =>
    err instanceof ApiError ? err.moTaRoRang : t('state.error.title');

  /*
   * Kết quả kiểm theo tên tệp — KHÔNG tự chạy khi mở tab (OQ-147).
   *
   * `POST …/verify` dựng một instance PGlite tạm và đọc hết mọi bảng: vài giây
   * CPU và vài chục MB bộ nhớ **mỗi lượt**. Kiểm sẵn cả danh sách nghĩa là mở
   * tab Sao lưu với 20 bản là dựng 20 instance cùng lúc — trên chính máy chủ
   * đang phục vụ dữ liệu thật. Nên kiểm là hành động người dùng chủ động,
   * từng bản một.
   *
   * Hệ quả: trạng thái ban đầu là **"chưa kiểm"**, và nó phải trông khác hẳn
   * "đạt". Một cột mặc định xanh cho tới khi chứng minh ngược lại là đúng cái
   * lỗi mà cột này sinh ra để chặn.
   */
  const [kiem, setKiem] = useState<Record<string, BackupCheck>>({});
  const [dangKiem, setDangKiem] = useState<string | null>(null);

  const nap = useCallback(async () => {
    try {
      setItems(await api.get<BackupFile[]>('/settings/backups'));
    } catch (err) {
      setError(err as ApiError);
    }
  }, []);

  useEffect(() => {
    void nap();
  }, [nap]);

  /*
   * Mọi thao tác đều phải NÓI ra kết quả — OQ-323.
   *
   * **Chủ dự án**: *"khi bấm sao lưu thì thành công hay có lỗi phải thông báo"*.
   *
   * Trước đây cả năm thao tác đều im lặng khi thành công: chúng chỉ nạp lại bảng
   * rồi thôi. Với "Sao lưu ngay" thì bảng có thêm một dòng — nhưng dòng ấy nằm
   * lẫn giữa những dòng cũ, và người vừa bấm phải tự đi tìm để biết mình có bấm
   * trúng không.
   *
   * Lỗi thì đã có `ApiErrorAlert`; thứ thiếu là vế còn lại.
   */
  async function chup(): Promise<void> {
    setBusy('create');
    setError(null);
    try {
      await api.post('/settings/backups');
      await nap();
      toast(t('settings.backup.createdToast'));
    } catch (err) {
      setError(err as ApiError);
      toastError(loiGon(err));
    } finally {
      setBusy(null);
    }
  }

  async function xoa(name: string): Promise<void> {
    setBusy(name);
    setError(null);
    try {
      await api.del(`/settings/backups/${encodeURIComponent(name)}`);
      setKiem((truoc) => {
        const sau = { ...truoc };
        delete sau[name];
        return sau;
      });
      await nap();
      toast(t('settings.backup.deletedToast', { name }));
    } catch (err) {
      setError(err as ApiError);
      toastError(loiGon(err));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Kiểm một bản sao lưu.
   *
   * Lỗi HTTP ở đây là *"phép kiểm không chạy được"* — khác hẳn *"bản sao lưu
   * hỏng"*, mà máy chủ trả về trong thân phản hồi với `ok: false`. Trộn hai thứ
   * lại sẽ báo tệp hỏng khi thật ra mạng rớt.
   */
  async function kiemTra(name: string): Promise<void> {
    setDangKiem(name);
    setError(null);
    try {
      const res = await api.post<BackupCheck>(
        `/settings/backups/${encodeURIComponent(name)}/verify`,
      );
      setKiem((truoc) => ({ ...truoc, [name]: res }));
      /* Kết quả nằm sẵn ở cột "Kiểm tra", nhưng bảng có thể dài và người vừa bấm
         đang nhìn cái nút chứ không nhìn cả bảng. Toast nói ngay phán quyết. */
      if (res.ok) toast(t('settings.backup.verifyOkToast', { name }));
      else toastError(t('settings.backup.verifyBadToast', { name }));
    } catch (err) {
      setError(err as ApiError);
      toastError(loiGon(err));
    } finally {
      setDangKiem(null);
    }
  }

  async function khoiPhuc(): Promise<void> {
    if (!xacNhan) return;
    setBusy('restore');
    setError(null);
    try {
      const res = await api.post<RestoreResult>(
        `/settings/backups/${encodeURIComponent(xacNhan.name)}/restore`,
      );
      setKetQua(res);
      setXacNhan(null);
      await nap();
      toast(t('settings.backup.restoredToast'));
    } catch (err) {
      setError(err as ApiError);
      toastError(loiGon(err));
    } finally {
      setBusy(null);
    }
  }

  async function taiLen(file: File): Promise<void> {
    setBusy('upload');
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      setKetQua(await api.upload<RestoreResult>('/settings/backups/upload', form));
      await nap();
      toast(t('settings.backup.restoredToast'));
    } catch (err) {
      setError(err as ApiError);
      toastError(loiGon(err));
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    /*
     * Cùng hình dạng với `BackupSchedulePanel` và `RemoteBackupPanel`: một
     * `.card`, không phải một `.tabpanel` riêng.
     *
     * Ba panel này đứng cạnh nhau trong tab Sao lưu. Panel này vốn tự bọc
     * `.tabpanel > .card-body` — di sản từ hồi nó là toàn bộ nội dung của tab —
     * nên khi có thêm hai panel bên cạnh, ba khối mang ba hình dạng khác nhau
     * và dính vào nhau không theo quy luật nào.
     */
    <div className="card" data-od-id="panel-backup">
      <div className="card-body">
        <div className="form-alert" role="note" style={{ marginBottom: 16 }}>
          <Icon name="info" size={16} />
          <span>{t('settings.backup.scope')}</span>
        </div>

        <ApiErrorAlert error={error} />

        {ketQua ? (
          <div className="form-alert ok" role="status" style={{ marginBottom: 16 }}>
            <Icon name="check" size={16} />
            <span>
              {t('settings.backup.restored', {
                rows: ketQua.rowsRestored,
                tables: ketQua.tables,
              })}
              {' · '}
              {t('settings.backup.safety', { name: ketQua.safetyBackup })}
            </span>
          </div>
        ) : null}

        <div className="row" style={{ gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary" onClick={chup} disabled={busy !== null}>
            <Icon name="download" size={16} />
            {busy === 'create' ? t('common.processing') : t('settings.backup.create')}
          </button>

          <button
            type="button"
            className="btn"
            onClick={() => fileRef.current?.click()}
            disabled={busy !== null}
          >
            <Icon name="upload" size={16} />
            {busy === 'upload' ? t('common.processing') : t('settings.backup.upload')}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".tgz,application/gzip"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void taiLen(f);
            }}
          />
        </div>

        <div className="table-wrap">
          <table className="tbl" data-od-id="backup-table">
            <thead>
              <tr>
                <th scope="col">{t('settings.backup.col.name')}</th>
                <th scope="col">{t('settings.backup.col.size')}</th>
                <th scope="col">{t('settings.backup.col.created')}</th>
                <th scope="col">{t('settings.backup.col.reason')}</th>
                <th scope="col">{t('settings.backup.col.check')}</th>
                <th scope="col" style={{ width: 1 }}>
                  {t('settings.backup.col.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {items === null ? (
                <tr>
                  <td colSpan={6}>{t('common.loading')}</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6}>{t('settings.backup.empty')}</td>
                </tr>
              ) : (
                items.map((b) => (
                  <tr key={b.name}>
                    <td>
                      <code>{b.name}</code>
                    </td>
                    <td>{coChu(b.sizeBytes)}</td>
                    <td>{new Date(b.createdAt).toLocaleString()}</td>
                    {/*
                      OQ-303 — vì sao bản này tồn tại. Ba loại trước đây trông y
                      hệt nhau, nên lúc cần chọn một bản để khôi phục — tức lúc
                      hoảng nhất — người ta phải đoán bản nào là bản mình vừa bấm
                      tay, bản nào là bản máy tự chụp trước khi ghi đè.
                    */}
                    <td className="small" data-od-id={`backup-reason-${b.name}`}>
                      {b.reason ? (
                        <span className="chip muted">
                          {t(`settings.backup.reason.${b.reason}`)}
                        </span>
                      ) : (
                        <span className="muted">{t('settings.backup.reason.unknown')}</span>
                      )}
                    </td>
                    <td data-od-id={`backup-check-${b.name}`}>
                      <OTrangThai check={kiem[b.name]} dangKiem={dangKiem === b.name} />
                    </td>
                    <td>
                      <div className="row" style={{ gap: 4 }}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => void kiemTra(b.name)}
                          disabled={busy !== null || dangKiem !== null}
                        >
                          <Icon name="refresh" size={14} />
                          {t('settings.backup.check')}
                        </button>
                        <a
                          className="btn btn-sm"
                          href={`${API_PREFIX}/settings/backups/${encodeURIComponent(b.name)}/download`}
                        >
                          {t('settings.backup.download')}
                        </a>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => setXacNhan(b)}
                          disabled={busy !== null}
                        >
                          {t('settings.backup.restore')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => void xoa(b.name)}
                          disabled={busy !== null}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {xacNhan ? (
        <XacNhanKhoiPhuc
          ten={xacNhan.name}
          check={kiem[xacNhan.name]}
          dangChay={busy !== null}
          onHuy={() => setXacNhan(null)}
          onXacNhan={() => void khoiPhuc()}
        />
      ) : null}
    </div>
  );
}
